/**
 * Console Logger Implementation
 *
 * A logger implementation that outputs to the browser console.
 * Supports configuration for log levels, timestamps, and prefixes.
 *
 * @module services/shared
 */

import { LogLevel } from './ILogger';
import type { ILogger, LoggerConfig, LogEntry } from './ILogger';

/**
 * Default configuration for ConsoleLogger
 */
const DEFAULT_CONFIG: Required<LoggerConfig> = {
  level: LogLevel.LOG,
  includeTimestamp: true,
  prefix: '',
  enabled: true
};

/**
 * Console logger implementation that outputs to browser console
 */
export class ConsoleLogger implements ILogger {
  private config: Required<LoggerConfig>;

  constructor(config: LoggerConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Check if a log level should be output based on configuration
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) {
      return false;
    }

    const levels = [LogLevel.DEBUG, LogLevel.LOG, LogLevel.WARN, LogLevel.ERROR];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);

    return messageLevelIndex >= currentLevelIndex;
  }

  /**
   * Format a log message with timestamp and prefix
   */
  private formatMessage(message: string): string {
    let formattedMessage = message;

    if (this.config.includeTimestamp) {
      const timestamp = new Date().toISOString();
      formattedMessage = `[${timestamp}] ${formattedMessage}`;
    }

    if (this.config.prefix) {
      formattedMessage = `${this.config.prefix} ${formattedMessage}`;
    }

    return formattedMessage;
  }

  /**
   * Create a log entry for advanced logging scenarios
   */
  private createLogEntry(level: LogLevel, message: string, args: unknown[]): LogEntry {
    return {
      level,
      message,
      args,
      timestamp: new Date(),
      prefix: this.config.prefix || undefined
    };
  }

  /**
   * Log a debug message
   */
  debug(message: string, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.DEBUG)) {
      return;
    }

    const formattedMessage = this.formatMessage(message);
    console.debug(formattedMessage, ...args);
  }

  /**
   * Log an informational message
   */
  log(message: string, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.LOG)) {
      return;
    }

    const formattedMessage = this.formatMessage(message);
    console.log(formattedMessage, ...args);
  }

  /**
   * Log a warning message
   */
  warn(message: string, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.WARN)) {
      return;
    }

    const formattedMessage = this.formatMessage(message);
    console.warn(formattedMessage, ...args);
  }

  /**
   * Log an error message
   */
  error(message: string, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.ERROR)) {
      return;
    }

    const formattedMessage = this.formatMessage(message);
    console.error(formattedMessage, ...args);
  }

  /**
   * Update logger configuration
   */
  updateConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current logger configuration
   */
  getConfig(): Required<LoggerConfig> {
    return { ...this.config };
  }

  /**
   * Get a log entry without outputting to console (for testing/advanced usage)
   */
  createEntry(level: LogLevel, message: string, ...args: unknown[]): LogEntry {
    return this.createLogEntry(level, message, args);
  }
}